import gp from '@sagold/json-pointer';
import {
  ArrayNode,
  Change,
  getNode,
  getNodeList,
  isJsonError,
  isNumberNode,
  JsonError,
  Node,
  PluginEvent,
} from '@sagold/react-json-editor';

export type CustomNode = Node & {
  [key: string]: string[];
};

export function isArrayNode(node: Node | JsonError): node is ArrayNode {
  return node.type === 'array' && node.children.length > 0;
}

function isCustomNode(
  node: Node,
  property: string,
  findInOptions: boolean
): node is CustomNode {
  if (findInOptions && node.schema.options) {
    return property in node.schema.options;
  }
  return property in node.schema;
}

export function getCustomNode(
  node: Node,
  property: string,
  findInOptions = false
) {
  return getNodeList(node).filter((node): node is CustomNode =>
    isCustomNode(node, property, findInOptions)
  );
}

export function isChangeEvent(e: PluginEvent): e is Change {
  return ['update', 'create', 'delete', 'validation'].includes(e.type);
}

export function calculateSum(
  paths: string[],
  rootNode: Node,
  sumNode: Node
): number {
  return paths
    .map((path) => {
      const node = getNode(rootNode, path);
      if (isArrayNode(node)) {
        const arrayNodes = getNodeList(node)
          .filter((n) => n.property === sumNode.property)
          .map((n) => n.pointer);
        return calculateSum(arrayNodes, rootNode, sumNode);
      }
      return isNumberNode(node) ? node.value : 0;
    })
    .reduceRight<number>((sum, value = 0) => sum + value, 0);
}

/**
 * defaults to search 1 level above
 */
export function getRelativePath(currentPath: string, toFindPath: string) {
  return gp.join(currentPath, '..', toFindPath);
}

export function getNodeFromJsonPath(
  currentPath: string,
  toFindPath: string,
  rootNode: Node
): Node | undefined {
  const path = getRelativePath(currentPath, toFindPath);
  const node = getNode(rootNode, path);
  if (!isJsonError(node)) {
    return node;
  }
  return;
}

export function checkPaths(
  currentPath: string,
  relativePaths: string[],
  eventPath: string
) {
  return relativePaths
    .map((path) => getRelativePath(currentPath, path))
    .some((path) => path.includes(eventPath));
}
